import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChildReaching } from '@fortawesome/pro-solid-svg-icons/faChildReaching';
import { faHouseChimneyHeart } from '@fortawesome/pro-solid-svg-icons/faHouseChimneyHeart';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faPeopleSimple } from '@fortawesome/pro-solid-svg-icons/faPeopleSimple';

import '../i18n/config';
import { Job } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { availabilityTypeConstant, bookingTypeConstant, bookingVerticalType } from '../constants';

export type Props = {
  job: Job;
  classes?: string;
  formatDate(bookingDatetime?: string): string;
  formatTime(bookingDatetime?: string): string;
  handleEditClick(id?: number): void;
  handleInterestClick(id?: number): void;
  handleSearchClick(job?: Job): void;
};

export const JobPostCard = ({
    job,
    classes = '',
    formatDate,
    formatTime,
    handleEditClick,
    handleInterestClick,
    handleSearchClick,
  }: Props): JSX.Element => {
  const { t } = useTranslation();

  const getIcon = (): IconDefinition => {
    const verticalTypeId = job.booking?.typeId ?? bookingVerticalType.CHILD_CARE;

    switch (verticalTypeId) {
      case bookingVerticalType.PET_CARE:
        return faPaw;

      case bookingVerticalType.HOUSEHOLD_SERVICES:
        return faHouseChimneyHeart;

      case bookingVerticalType.SENIOR_CARE:
        return faPeopleSimple;

      case bookingVerticalType.CHILD_CARE:
      default:
        return faChildReaching;
    }
  }

  // TODO: Refactor these to use shared components/utils/date.ts
  const isPastDate = (date: Date): boolean => {
    const now = new Date();

    return date.getFullYear() < now.getFullYear()
      || (date.getFullYear() === now.getFullYear() && date.getMonth() < now.getMonth())
      || (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() < now.getDate());
  }
  const isSameDate = (date1: Date, date2?: Date): boolean => {
    if (!date2) { return false }

    return date1.getFullYear() === date2.getFullYear()
      && date1.getMonth() === date2.getMonth()
      && date1.getDate() === date2.getDate();
  }
  const isToday = (date: Date): boolean => {
    const now = new Date();

    return isSameDate(date, now);
  }

  const getHeadline = (): string => {
    const verticalId = job.booking?.typeId ?? bookingVerticalType.CHILD_CARE;
    const lookingFor = bookingTypeConstant.find((b: { id: number; }) => b.id === verticalId)!.label;
    const jobStart = new Date(job.booking?.start ?? '');
    const jobEnd = job.booking?.end ? new Date(job.booking!.end!) : undefined;
    const isOneTime = job.type === 'one_time';
    const isRecurring = job.type === 'recurring';

    let dateString = '';
    let jobType = `${lookingFor}`;

    if (isRecurring && job.booking?.typeId !== bookingVerticalType.HOUSEHOLD_SERVICES) {
      const frequency = availabilityTypeConstant.find((a: { type?: string; }) => a.type === job.availability)?.label;

      jobType += `, ${frequency}`;
    }

    if (isOneTime) {
      // return "day of week, start date/time - end date/time"
      dateString = `${isToday(jobStart) ? 'TODAY' : formatDate(job.booking!.start!)} ${formatTime(job.booking!.start!)} - `;

      if (!isSameDate(jobStart, jobEnd)) {
        dateString += `${formatDate(job.booking!.end!)} `;
      }

      dateString += `${formatTime(job.booking!.end!)}`;
    } else {
      // list recurring availability (part or full time)
      const recurring = 'starting';

      if (isToday(jobStart)) {
        dateString += `${recurring} TODAY`;
      } else if (isPastDate(jobStart)) {
        dateString += `${recurring} ASAP`;
      } else if (!jobEnd) {
        dateString += `${recurring} ${formatDate(job.booking!.start)}`;
      } else {
        dateString += `${formatDate(job.booking!.start!)}`;

        if (!isSameDate(jobStart, jobEnd)) {
          dateString += ` - ${formatDate(job.booking!.end!)}`;
        }
      }
    }

    return `${jobType}, ${dateString}`;
  }

  const getTitle = (): string => {
    if (job.numberOfApplicants && job.numberOfApplicants > 0) {
      return `${job.numberOfApplicants}
        ${job.numberOfApplicants === 1
          ? t('components.jobPostCard.hasApplied')
          : t('components.jobPostCard.haveApplied')}`;
    }
      return t('components.jobPostCard.noApplicants');

  };

  return (
    <div className={`${classes}`}>
      <div className='flex relative'>
        <div className='mt-2 mr-2'>
          <FontAwesomeIcon size='3x' icon={getIcon()} className='text-emerald' />
        </div>
        <div className='flex-1'>
          <div className='font-bold text-sm'>{ getTitle() }</div>
          <div className='text-sm'>{ getHeadline() }</div>
        </div>
      </div>
      <div className='flex-1'>
        { !job.numberOfApplicants && <div className='text-xs mt-2'>{t('components.jobPostCard.savedSearchUpsell')}</div> }
        <div className='flex justify-between'>
          { job.numberOfApplicants ? (
            <Button
              buttonType={ButtonType.primary}
              className='mt-3 mb-1 justify-center'
              onClick={(): void => { handleInterestClick(job?.id) }}>
                {t('components.jobPostCard.viewApplicants')}
            </Button>
          ) : (
            <Button
              buttonType={ButtonType.primary}
              className='inline-flex mt-3 mb-1 justify-center'
              onClick={(): void => { handleSearchClick(job) }}>
                {t('common.search')}
            </Button>
          )}
          <Button
            buttonType={ButtonType.tertiary}
            className='mt-2'
            onClick={(): void => { handleEditClick(job?.id) }}>
              {t('components.jobPostCard.edit')}
              <FontAwesomeIcon size='sm' icon={faAngleRight} className='text-sapphire pl-2 m-[-1px]' />
          </Button>
        </div>
      </div>
    </div>
  );
};
